/**
 * GrowthTraites — iter109 · Vue centrale des décisions post-préqualification
 *
 * **Brief CEO §2** :
 *  "UNE page centrale /growth/traites qui rassemble les décisions prises
 *  depuis le parcours Growth. Onglets : Tous | Qualifiés | Vivier | Rappels |
 *  Injoignables | Écartés | Refusés"
 *
 *  "Filtres : Annonce | Décision | Aujourd'hui | Cette semaine | Ce mois |
 *   Période personnalisée | Recruteur"
 *
 *  "Je veux notamment pouvoir retrouver immédiatement :
 *   'Tous les candidats de l'annonce Business Development mis au Vivier en septembre 2026.'"
 *
 * Source unique de vérité : collection `growth_decisions` (audit trail
 * immuable écrit par chaque `POST /growth/candidates/{id}/decision-*`).
 */
import { motion } from "framer-motion";
import {
    Archive, CalendarClock, CheckCircle2, ChevronDown, ExternalLink,
    FileText, Filter, Loader2, Search, Star, UserX, X, XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../lib/api";


const TAB_META = {
    all:         { label: "Tous",          emoji: "📋", color: "bg-slate-600" },
    qualified:   { label: "Qualifiés",     emoji: "✅", color: "bg-emerald-500", icon: CheckCircle2 },
    talent_pool: { label: "Vivier ⭐",     emoji: "⭐", color: "bg-purple-500",  icon: Star },
    recall:      { label: "Rappels",       emoji: "📅", color: "bg-blue-500",    icon: CalendarClock },
    unavailable: { label: "Injoignables",  emoji: "🚫", color: "bg-amber-500",   icon: UserX },
    set_aside:   { label: "Écartés",       emoji: "📁", color: "bg-slate-700",   icon: Archive },
    rejected:    { label: "Refusés",       emoji: "❌", color: "bg-rose-500",    icon: XCircle },
};

const PERIODS = [
    { key: "today",    label: "Aujourd'hui" },
    { key: "week",     label: "Cette semaine" },
    { key: "month",    label: "Ce mois" },
    { key: "all",      label: "Toute la période" },
    { key: "custom",   label: "Personnalisée" },
];

function periodToDates(period) {
    const now = new Date();
    if (period === "today") {
        const start = new Date(now); start.setHours(0, 0, 0, 0);
        return { from: start.toISOString(), to: null };
    }
    if (period === "week") {
        const start = new Date(now); start.setDate(now.getDate() - 7);
        return { from: start.toISOString(), to: null };
    }
    if (period === "month") {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: start.toISOString(), to: null };
    }
    return { from: null, to: null };
}

function formatDT(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}


export default function GrowthTraites() {
    const [tab, setTab] = useState("all");
    const [period, setPeriod] = useState("month");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [offerFilter, setOfferFilter] = useState("");
    const [recruteurFilter, setRecruteurFilter] = useState("");
    const [search, setSearch] = useState("");

    const [data, setData] = useState({ decisions: [], counts: {}, offers: [], recruteurs: [] });
    const [loading, setLoading] = useState(true);

    const params = useMemo(() => {
        const p = {};
        if (tab !== "all") p.decision_type = tab;
        if (offerFilter) p.offer_id = offerFilter;
        if (recruteurFilter) p.decision_by_id = recruteurFilter;
        if (period === "custom") {
            if (customFrom) p.from_date = new Date(customFrom).toISOString();
            if (customTo) p.to_date = new Date(customTo).toISOString();
        } else if (period !== "all") {
            const { from, to } = periodToDates(period);
            if (from) p.from_date = from;
            if (to) p.to_date = to;
        }
        p.limit = 500;
        return p;
    }, [tab, period, customFrom, customTo, offerFilter, recruteurFilter]);

    useEffect(() => {
        setLoading(true);
        api.get("/growth/decisions", { params })
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    }, [params]);

    // Recherche locale (nom, email)
    const filtered = useMemo(() => {
        if (!search.trim()) return data.decisions;
        const q = search.toLowerCase();
        return data.decisions.filter((d) => {
            const snap = d.candidate_snapshot || {};
            const full = `${snap.first_name || ""} ${snap.last_name || ""} ${snap.email || ""}`.toLowerCase();
            return full.includes(q);
        });
    }, [data.decisions, search]);

    // Offre lookup : offer_id inconnu côté frontend, on filtre par offer_title
    // (les offres retournées par le backend sont des titres distincts)
    const offerOptions = data.offers || [];
    const recruteurOptions = data.recruteurs || [];

    // Récupérer l'offer_id à partir du titre (utile pour la query)
    // Ici on filtre côté backend par offer_id mais l'API expose offer_title dans la liste.
    // Simplification : on filtre côté frontend en supplément.
    const filteredByOffer = useMemo(() => {
        if (!offerFilter) return filtered;
        return filtered.filter((d) => d.offer_title === offerFilter);
    }, [filtered, offerFilter]);

    return (
        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="growth-traites-page">

            {/* HEADER */}
            <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-700 border border-purple-200 text-[10px] font-black uppercase tracking-widest mb-2">
                    <FileText className="h-3 w-3" /> Audit trail Growth
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-shariti-black tracking-tight">Candidats traités</h1>
                <p className="text-base font-semibold text-slate-500 mt-1">
                    Toutes les décisions prises depuis le mode préqualif Growth · immuables · filtrables
                </p>
            </div>

            {/* ONGLETS */}
            <div className="flex flex-wrap gap-2 border-b border-slate-100 pb-3">
                {Object.entries(TAB_META).map(([key, meta]) => {
                    const count = key === "all" ? (data.counts.all || 0) : (data.counts[key] || 0);
                    const isActive = tab === key;
                    return (
                        <button key={key} onClick={() => setTab(key)}
                            data-testid={`growth-traites-tab-${key}`}
                            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-black transition border-2 ${
                                isActive
                                    ? `${meta.color} text-white border-transparent`
                                    : "bg-white text-slate-600 border-slate-200 hover:border-shariti-orange hover:text-shariti-orange"
                            }`}>
                            <span>{meta.emoji}</span>
                            {meta.label}
                            <span className={`ml-1 px-1.5 rounded-full text-[10px] font-black ${
                                isActive ? "bg-white/20" : "bg-slate-100 text-slate-500"
                            }`}>{count}</span>
                        </button>
                    );
                })}
            </div>

            {/* FILTRES */}
            <div className="rounded-2xl bg-white border-2 border-slate-100 p-4 space-y-3" data-testid="growth-traites-filters">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-1 flex items-center gap-1">
                        <Filter className="h-3 w-3" /> Filtres :
                    </span>

                    {/* Période */}
                    <div className="flex items-center gap-1">
                        {PERIODS.map((p) => (
                            <button key={p.key} onClick={() => setPeriod(p.key)}
                                data-testid={`growth-traites-period-${p.key}`}
                                className={`h-8 px-3 rounded-full text-xs font-bold border transition ${
                                    period === p.key
                                        ? "bg-shariti-orange text-white border-shariti-orange"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-shariti-orange hover:text-shariti-orange"
                                }`}>{p.label}</button>
                        ))}
                    </div>
                </div>

                {period === "custom" && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Du :</label>
                        <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                            data-testid="growth-traites-custom-from"
                            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold" />
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Au :</label>
                        <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                            data-testid="growth-traites-custom-to"
                            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold" />
                    </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Annonce */}
                    <select value={offerFilter} onChange={(e) => setOfferFilter(e.target.value)}
                        data-testid="growth-traites-filter-offer"
                        className="h-8 rounded-full border border-slate-200 px-3 text-xs font-bold bg-white text-slate-600 hover:border-shariti-orange focus:outline-none">
                        <option value="">Toutes les annonces</option>
                        {offerOptions.map((o, i) => (<option key={i} value={o}>{o}</option>))}
                    </select>

                    {/* Recruteur */}
                    <select value={recruteurFilter} onChange={(e) => setRecruteurFilter(e.target.value)}
                        data-testid="growth-traites-filter-recruteur"
                        className="h-8 rounded-full border border-slate-200 px-3 text-xs font-bold bg-white text-slate-600 hover:border-shariti-orange focus:outline-none">
                        <option value="">Tous les recruteurs</option>
                        {recruteurOptions.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
                    </select>

                    {/* Recherche libre */}
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                            data-testid="growth-traites-search"
                            placeholder="Rechercher (nom, email)…"
                            className="w-full h-8 pl-8 pr-3 rounded-full border border-slate-200 text-xs font-bold focus:outline-none focus:border-shariti-orange" />
                    </div>

                    {/* Reset */}
                    {(offerFilter || recruteurFilter || search) && (
                        <button onClick={() => { setOfferFilter(""); setRecruteurFilter(""); setSearch(""); }}
                            data-testid="growth-traites-reset-filters"
                            className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-bold text-slate-500 hover:text-shariti-orange transition">
                            <X className="h-3 w-3" />Réinitialiser
                        </button>
                    )}
                </div>
            </div>

            {/* LISTE */}
            {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : filteredByOffer.length === 0 ? (
                <div className="text-center py-16 rounded-2xl bg-slate-50 border border-slate-100">
                    <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="font-bold text-slate-500">Aucune décision sur cette période</p>
                    <p className="text-xs text-slate-400 mt-1">Les décisions prises depuis le mode préqualif <Link to="/growth" className="text-shariti-orange font-bold">/growth</Link> apparaîtront ici.</p>
                </div>
            ) : (
                <div className="space-y-2" data-testid="growth-traites-list">
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">
                        {filteredByOffer.length} décision{filteredByOffer.length > 1 ? "s" : ""} trouvée{filteredByOffer.length > 1 ? "s" : ""}
                    </p>
                    {filteredByOffer.map((dec, i) => <DecisionRow key={dec.id} d={dec} i={i} />)}
                </div>
            )}
        </div>
    );
}


function DecisionRow({ d, i }) {
    const meta = TAB_META[d.decision_type] || TAB_META.all;
    const snap = d.candidate_snapshot || {};
    return (
        <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
            data-testid={`growth-traites-row-${d.id}`}
            className="rounded-xl bg-white border-2 border-slate-100 hover:border-shariti-orange/40 p-4 transition">
            <div className="flex items-start gap-4 flex-wrap">
                {/* Badge décision */}
                <div className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest text-white ${meta.color}`}>
                    <span>{meta.emoji}</span>{meta.label}
                </div>

                {/* Nom + offre */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-black text-shariti-black truncate">{snap.first_name} {snap.last_name}</h3>
                        {snap.growth_score != null && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-shariti-cream text-shariti-orange text-[10px] font-black">Score {snap.growth_score}/100</span>
                        )}
                    </div>
                    <p className="text-xs font-semibold text-slate-500 mt-0.5 truncate">
                        {d.offer_title || "—"} · {snap.email || snap.phone || "—"}
                    </p>
                    <p className="text-[10px] font-semibold text-slate-400 mt-0.5">
                        {formatDT(d.decision_at)} · par <span className="text-shariti-orange">{d.decision_by_name || "—"}</span>
                        {d.reason_label && <span> · motif : <b>{d.reason_label}</b></span>}
                        {d.recall_at && <span className="text-blue-600"> · rappel prévu {formatDT(d.recall_at)}</span>}
                    </p>
                    {d.note && (
                        <p className="text-[11px] italic text-slate-500 mt-1 border-l-2 border-slate-200 pl-2">« {d.note} »</p>
                    )}
                </div>

                {/* Actions */}
                <div className="shrink-0 flex items-center gap-2">
                    {snap.cv_url ? (
                        <a href={snap.cv_url} target="_blank" rel="noopener noreferrer"
                            data-testid={`growth-traites-cv-${d.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-shariti-orange text-white hover:bg-shariti-orange-hover transition">
                            <FileText className="h-3 w-3" />CV<ExternalLink className="h-2.5 w-2.5" />
                        </a>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-slate-100 text-slate-400"><FileText className="h-3 w-3" />Pas de CV</span>
                    )}
                    <Link to={`/growth/${d.candidate_id}/prequal`} data-testid={`growth-traites-open-${d.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-white border-2 border-slate-200 text-slate-700 hover:border-shariti-orange hover:text-shariti-orange transition">
                        Ouvrir la fiche<ChevronDown className="h-3 w-3 -rotate-90" />
                    </Link>
                </div>
            </div>
        </motion.div>
    );
}
